/**
 * Legacy-to-shell route transformations.
 * When the shell is served for a legacy URL (via feature toggle), the browser URL
 * still shows the legacy path. This map defines how to transform the legacy prefix
 * into the shell's route structure before Angular bootstraps.
 *
 * Format: { legacyPrefix: shellPrefix }
 * - The legacy prefix is stripped from the URL
 * - The shell prefix is prepended to the remaining path
 * - Longest prefix match is used
 */
export const ROUTE_TRANSFORMS: Record<string, string> = {
  ['/auth']: '',
  ['/dashboard/apps']: '/apps',
  ['/dashboard']: '/apps',
  ['/app-details']: '/apps',
  ['/platform-documentation']: '/documentation',
  ['/home']: '/home',
};

const COLLAPSED_ROUTE_TRANSFORMS: readonly (readonly [string, string])[] = [
  ['/app-details/file-transfer', '/transfers'],
  ['/auth/sign-in/forgot-password', '/password-reset'],
  ['/dashboard/file-transfers/new', '/apps'],
  ['/dashboard/register', '/apps'],
  ['/resources/our-process', '/'],
  ['/auth/signed-out', '/'],
  ['/auth/otp', '/sign-in'],
  ['/help/contact-us', '/help/tickets'],
  ['/help/faq', '/help/tickets'],
  ['/file-transfer', '/transfers'],
  ['/support', '/help/tickets'],
  ['/faq', '/help/tickets'],
  ['/products', '/solutions'],
  ['/product', '/solutions'],
];

const EXTERNAL_ROUTE_TRANSFORMS: Readonly<Record<string, string>> = {
  ['/resources/tech']: 'https://www.capitalone.com/tech',
};

/**
 * Transform a legacy URL path to the shell's route path.
 * If the path matches a legacy prefix, strip it and prepend the shell prefix.
 * If no match, return the path as-is.
 */
export function transformRoute(pathname: string): string {
  const legacyTryItNowPath = '/documentation/try-it-now';
  if (pathname === legacyTryItNowPath || pathname === `${legacyTryItNowPath}/`) return '/solutions';

  const legacyAccountPath = '/dashboard/user-settings';
  if (pathname === legacyAccountPath || pathname === `${legacyAccountPath}/`) return '/account';

  const legacyTransfersPath = '/dashboard/file-transfers';
  if (pathname === legacyTransfersPath || pathname === `${legacyTransfersPath}/`)
    return '/transfers';

  const legacySchemaMatch = /^\/app-details\/([^/]+)\/schema\/[^/]+\/?$/.exec(pathname);
  if (legacySchemaMatch) {
    return `/apps/${legacySchemaMatch[1]}`;
  }

  if (pathname === '/sandbox' || pathname === '/sandbox/') {
    return '/solutions';
  }

  for (const [prefix, destination] of COLLAPSED_ROUTE_TRANSFORMS) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return destination;
    }
  }

  const prefixes = Object.keys(ROUTE_TRANSFORMS).sort((a, b) => b.length - a.length);

  for (const prefix of prefixes) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      const remainder = pathname.slice(prefix.length);
      const result = ROUTE_TRANSFORMS[prefix] + remainder;
      return result === '' ? '/' : result;
    }
  }

  return pathname;
}

export function getExternalRedirect(pathname: string): string | null {
  const normalized = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return EXTERNAL_ROUTE_TRANSFORMS[normalized] ?? null;
}
